import React, { useEffect, useRef, useState } from 'react';
import { colors } from '../theme';

const GOOGLE_MAPS_API_KEY = 'AIzaSyAEaXTbKtYXG5cbG7PPLu1ilEdm6atJL0U';

// Load Google Maps script once
let scriptLoaded = false;
let scriptLoading = false;
const scriptCallbacks: Array<() => void> = [];

function loadGoogleMapsScript(): Promise<void> {
  return new Promise((resolve) => {
    if (scriptLoaded && window.google?.maps?.places) {
      resolve();
      return;
    }

    scriptCallbacks.push(resolve);

    if (!scriptLoading) {
      scriptLoading = true;
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&v=weekly`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        scriptLoaded = true;
        scriptCallbacks.forEach((cb) => cb());
        scriptCallbacks.length = 0;
      };
      script.onerror = () => {
        scriptLoading = false;
        console.error('Failed to load Google Maps');
      };
      document.head.appendChild(script);
    }
  });
}

export interface PlaceResult {
  address: string;
  lat: number;
  lng: number;
  city: string;
  state: string;
  zip: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect?: (place: PlaceResult) => void;
  placeholder?: string;
  label?: string;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  autoFocus?: boolean;
}

export default function AddressAutocomplete({
  value,
  onChange,
  onPlaceSelect,
  placeholder = '123 Main St, City, State 12345',
  label,
  style,
  inputStyle,
  autoFocus,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    loadGoogleMapsScript()
      .then(() => setIsLoaded(true))
      .catch((e) => {
        console.warn('Google Maps Places not available — using plain input fallback');
        setIsLoaded(false); // stay as plain input
      });
  }, []);

  useEffect(() => {
    if (!isLoaded || !inputRef.current || autocompleteRef.current) return;
    if (!(window as any).google?.maps?.places) return;

    const google = (window as any).google;
    const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      componentRestrictions: { country: 'us' },
      fields: ['formatted_address', 'geometry', 'address_components'],
    });

    autocompleteRef.current = autocomplete;

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place?.formatted_address || !place?.geometry?.location) return;

      const addr = place.formatted_address;
      onChange(addr);
      setVerified(true);

      if (onPlaceSelect) {
        let city = '';
        let state = '';
        let zip = '';
        (place.address_components || []).forEach((comp: any) => {
          if (comp.types.includes('locality')) city = comp.long_name;
          if (comp.types.includes('administrative_area_level_1')) state = comp.short_name;
          if (comp.types.includes('postal_code')) zip = comp.long_name;
        });

        onPlaceSelect({
          address: addr,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          city,
          state,
          zip,
        });
      }
    });
  }, [isLoaded]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVerified(false);
    onChange(e.target.value);
  };

  const borderColor = verified
    ? colors.success
    : isFocused
    ? colors.primary
    : 'rgba(26,10,46,0.1)';

  return (
    <div style={style}>
      {label && (
        <label style={{ color: colors.textSecondary, fontSize: 13, display: 'block', marginBottom: 8 }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <span
          style={{
            position: 'absolute',
            left: 13,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 16,
            pointerEvents: 'none',
          }}
        >
          🏠
        </span>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
          aria-autocomplete="list"
          role="combobox"
          aria-expanded="false"
          style={{
            width: '100%',
            padding: '13px 40px 13px 40px',
            background: colors.surface,
            border: `1.5px solid ${borderColor}`,
            borderRadius: 12,
            color: colors.textPrimary,
            fontSize: 15,
            outline: 'none',
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            boxSizing: 'border-box',
            transition: 'border-color 0.2s',
            ...inputStyle,
          }}
        />
        {verified && (
          <span
            style={{
              position: 'absolute',
              right: 13,
              top: '50%',
              transform: 'translateY(-50%)',
              color: colors.success,
              fontSize: 16,
            }}
          >
            ✓
          </span>
        )}
      </div>
      {verified ? (
        <p style={{ color: colors.success, fontSize: 12, marginTop: 4 }}>✓ Address verified</p>
      ) : isLoaded && value.length > 5 ? (
        <p style={{ color: colors.textSecondary, fontSize: 11, marginTop: 4 }}>
          Select a suggestion from the dropdown to verify
        </p>
      ) : null}
    </div>
  );
}
